import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import html2canvas from 'html2canvas';
import { ArrowLeft, Download, Printer, Eye } from 'lucide-react';
import { getUser } from '../../auth/Auth';
import { loadWish } from '../../common/Common';

function PrintQR() {
    const { id } = useParams(); // numeric DB id from /portal/print/:id
    const navigate = useNavigate();
    const printRef = useRef(null);

    const [qrData, setQrData] = useState(null);
    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [loading, setLoading] = useState(true);

    const wishTypes = {
        birthday: '🎂 Birthday',
        anniversary: '❤️ Anniversary',
        congratulations: '🎉 Congratulations',
        retirement: '🏆 Retirement',
        graduation: '🎓 Graduation',
        wedding: '💒 Wedding',
        baby: '👶 Baby Shower',
        love: '💕 Love Note'
    };

    // Load wish from API and generate QR
    useEffect(() => {
        let mounted = true;

        (async () => {
            try {
                const user = await getUser();
                if (!mounted) return;

                const w = await loadWish(id, user?.token);
                if (!mounted || !w) return;

                // Map API -> UI (do NOT append "Z" to avoid UTC shift)
                const mapped = {
                    id: w.id,
                    type: w.type,
                    receiverName: w.receiver_name,
                    senderName: w.sender_name,
                    message: w.message,
                    templateId: w.page_template,
                    qrToken: w.qr_token,
                    createdAt: w.created_at ? new Date(w.created_at) : null,
                    // "YYYY-mm-dd HH:ii:ss" -> "YYYY-mm-ddTHH:ii:ss" local time
                    expiresAt: w.expires_at ? new Date(w.expires_at.replace(' ', 'T')) : null
                };

                setQrData(mapped);

                if (!mapped.qrToken) {
                    console.error('Wish is missing qr_token');
                    return;
                }

                // Public page the QR should open
                const wishUrl = `${window.location.origin}/wish/${mapped.qrToken}`;

                // Generate QR image
                const dataUrl = await QRCode.toDataURL(wishUrl, {
                    width: 300,
                    margin: 1,
                    color: { dark: '#000000', light: '#FFFFFF' }
                });

                if (!mounted) return;
                setQrCodeUrl(dataUrl);
            } catch (e) {
                console.error(e);
            } finally {
                if (mounted) setLoading(false);
            }
        })();

        return () => { mounted = false; };
    }, [id]);

    const handlePrint = () => window.print();

    const handleDownload = async () => {
        if (!printRef.current || !qrData) return;
        setIsGenerating(true);
        try {
            const canvas = await html2canvas(printRef.current, {
                scale: 2,
                backgroundColor: '#ffffff'
            });
            const link = document.createElement('a');
            const safeReceiver = (qrData.receiverName || 'recipient').replace(/\s+/g, '-');
            link.download = `keek-qr-${qrData.type}-${safeReceiver}.png`;
            link.href = canvas.toDataURL();
            link.click();
        } catch (err) {
            console.error('Failed to generate image:', err);
        } finally {
            setIsGenerating(false);
        }
    };

    const handlePreview = () => {
        if (!qrData?.qrToken) return;
        window.open(`/wish/${qrData.qrToken}`, '_blank');
    };

    if (loading || !qrData) {
        return (
            <div className="p-8 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading QR code...</p>
                </div>
            </div>
        );
    }

    const expiresText =
        qrData.expiresAt instanceof Date && !isNaN(qrData.expiresAt)
            ? qrData.expiresAt.toLocaleString()
            : 'in 24 hours';

    return (
        <div className="p-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                    <button
                        onClick={() => navigate('/portal/qrs')}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                    >
                        <ArrowLeft className="w-6 h-6" />
                    </button>
                    <div className="flex-1">
                        <h1 className="text-3xl font-bold text-gray-800">Print QR Code</h1>
                        <p className="text-gray-600">Ready to print your custom wish QR code</p>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={handlePreview}
                            className="flex items-center gap-2 px-4 py-2 text-blue-600 bg-blue-100 rounded-xl font-medium hover:bg-blue-200 transition-colors duration-200"
                        >
                            <Eye className="w-5 h-5" />
                            Preview
                        </button>

                        <button
                            onClick={handleDownload}
                            disabled={isGenerating}
                            className="flex items-center gap-2 px-4 py-2 text-green-600 bg-green-100 rounded-xl font-medium hover:bg-green-200 transition-colors duration-200 disabled:opacity-50"
                        >
                            {isGenerating ? (
                                <div className="w-5 h-5 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <Download className="w-5 h-5" />
                            )}
                            Download
                        </button>

                        <button
                            onClick={handlePrint}
                            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300"
                        >
                            <Printer className="w-5 h-5" />
                            Print
                        </button>
                    </div>
                </div>

                {/* QR Code Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-2xl shadow-2xl overflow-hidden"
                >
                    <div
                        id="print-content"
                        ref={printRef}
                        className="p-12 text-center bg-gradient-to-br from-orange-50 to-pink-50"
                    >
                        {/* Header */}
                        <div className="mb-8">
                            <div className="flex items-center justify-center gap-3 mb-4">
                                <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-full w-12 h-12 flex items-center justify-center">
                                    <span className="text-white text-xl font-bold">K</span>
                                </div>
                                <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                                    Keek Colombo
                                </h2>
                            </div>
                            <p className="text-gray-600 text-lg">Sweet Wishes Delivered</p>
                        </div>

                        {/* QR Code */}
                        <div className="bg-white rounded-3xl p-8 shadow-xl mb-8 inline-block">
                            {qrCodeUrl && (
                                <img
                                    src={qrCodeUrl}
                                    alt="QR Code"
                                    className="w-72 h-72 mx-auto"
                                />
                            )}
                        </div>

                        {/* Details */}
                        <div className="space-y-4 max-w-md mx-auto">
                            <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 text-left">
                                <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">
                                    {wishTypes[qrData.type] || qrData.type}
                                </h3>

                                <div className="space-y-3 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">For:</span>
                                        <span className="font-semibold text-gray-800">{qrData.receiverName}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">From:</span>
                                        <span className="font-semibold text-gray-800">{qrData.senderName}</span>
                                    </div>
                                    <div className="border-t pt-3">
                                        <p className="text-gray-700 italic text-center">
                                            "{qrData.message}"
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="text-center">
                                <p className="text-sm text-gray-600 mb-1">
                                    Scan to view your personalized wish
                                </p>
                                <p className="text-xs text-orange-600 font-medium">
                                    Valid until {expiresText} • Created with 💕 by Keek Colombo
                                </p>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Instructions */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6"
                >
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Printing Instructions</h3>
                    <div className="grid md:grid-cols-3 gap-4 text-sm">
                        <div>
                            <h4 className="font-medium text-gray-800 mb-2">📱 For Mobile Users</h4>
                            <p className="text-gray-600">Use the "Download" button to save the image, then print from your photos app</p>
                        </div>
                        <div>
                            <h4 className="font-medium text-gray-800 mb-2">🖨️ For Desktop Users</h4>
                            <p className="text-gray-600">Click "Print" for direct printing, or download as PNG for later use</p>
                        </div>
                        <div>
                            <h4 className="font-medium text-gray-800 mb-2">📏 Best Results</h4>
                            <p className="text-gray-600">Print on A4 paper in portrait mode for optimal QR code scanning</p>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Print-only CSS */}
            <style jsx="true">{`
        @media print {
          body * { visibility: hidden; }
          #print-content, #print-content * { visibility: visible; }
          #print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
        </div>
    );
}

export default PrintQR;
